import { AfterViewInit, Component, Inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent } from '@angular/material/dialog';
import { PrepLocationsService } from 'src/app/services/prep-locations.service';
import { IPrepLocation } from 'src/app/common/models/prep-location';
import { ToastrService } from 'ngx-toastr';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { IMultiStoreSelect } from 'src/app/common/models/multiStoreSelect';
import { FacilityItemInformation } from 'src/app/common/models/facilityItemInformation';
import { MultiItemGroupSelect } from 'src/app/common/models/multiItemSelect';
import { BatchPatchRequest, ItemsService } from 'src/app/services/items.service';
import { catchError, forkJoin, map, Observable, of, timer, switchMap, takeWhile } from 'rxjs';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { StatusService } from 'src/app/services/status.service';
import { LoggingService } from 'src/app/services/logging.service';
import { ItemGroup } from 'src/app/common/models/itemGroup';
import { AuthManager } from 'src/app/core/auth/auth.manager';
import { itemDatesValidator } from 'src/app/modules/shared/item-validator.directive';
import { maxStartDate, minEndDate } from 'src/app/modules/shared/item-date-rules';
import { MatCheckbox } from '@angular/material/checkbox';
import { ItemGroupEntity } from 'src/app/common/models/itemGroupEntity';
import { ItemGroupHelpers } from 'src/app/common/itemGroupHelpers';
import { AdminUIHelpers } from 'src/app/common/helpers';
import { Operation } from 'src/app/common/models/operation';
import {
  MatStepper,
  MatStep,
  MatStepLabel,
  MatStepContent,
  MatStepperPrevious,
  MatStepperNext,
} from '@angular/material/stepper';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatFormField, MatLabel, MatHint, MatInput, MatSuffix } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatDatepickerToggle, MatDatepicker, MatDatepickerInput } from '@angular/material/datepicker';
import { MatButton } from '@angular/material/button';
import { IncrementInputComponent } from 'src/app/modules/shared/components/increment-input/increment-input.component';
import { MultiItemChecklist } from '../../multi-item-checklist/multi-item-checklist.component';
import { MultiStoreDropdown } from '../../multi-store-dropdown/multi-store-dropdown.component';
import { formatDate } from '@angular/common';

interface PatchResult {
  isError: boolean;
  error: any;
  successCount?: number;
  errorCount?: number;
}

interface FieldChange {
  label: string;
  description: string;
}

const HANDOFF_CONTROL_TO_API: Record<string, string> = {
  carryout: 'store',
  curbside: 'curbside',
  delivery: 'delivery',
  kiosk: 'kiosk',
  tableside: 'tableside',
};

function atLeastOneSelectedValidator(): ValidatorFn {
  return (g: AbstractControl): ValidationErrors | null => {
    const group = g as FormGroup;
    return Object.values(group.controls).some((c) => !!c.value) ? null : { noneSelected: true };
  };
}

@Component({
  templateUrl: './edit-item.component.html',
  styleUrls: ['./edit-item.component.scss'],
  standalone: true,
  imports: [
    MatDialogContent,
    MatStepper,
    MatStep,
    FormsModule,
    ReactiveFormsModule,
    MatStepLabel,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatHint,
    IncrementInputComponent,
    MatRadioGroup,
    MatRadioButton,
    MatInput,
    MatSuffix,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatCheckbox,
    MatButton,
    MatStepperPrevious,
    MatStepperNext,
    MatStepContent,
    MultiItemChecklist,
    MultiStoreDropdown,
  ],
})
export class EditItemComponent implements AfterViewInit {
  public editItemForm!: FormGroup;
  public isClickable = true;

  public initialDateRestrictions: boolean;
  public currentFacilityId: string;
  public prepLocations: IPrepLocation[] = [];
  public facilities: IMultiStoreSelect[] = [];
  public selectedItemGroups: MultiItemGroupSelect[] = [];
  public baseItemGroupEntity: ItemGroupEntity;
  public baseItemGroups: ItemGroup[];
  public menuItems: FacilityItemInformation[];
  public itemChangeState: FacilityItemInformation;
  public baseItem: FacilityItemInformation;
  public updatedItemGroups: ItemGroup[] = [];
  public adminOrITRole: boolean;
  public changeSummary: FieldChange[] = [];

  private baselineSnapshot!: any;

  constructor(
    public dialogRef: MatDialogRef<EditItemComponent>,
    private readonly authManager: AuthManager,
    private readonly itemService: ItemsService,
    private readonly _facilityManager: FacilityManager,
    private readonly prepLocationService: PrepLocationsService,
    private readonly logger: LoggingService,
    private readonly toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: { itemGroupEntity: ItemGroupEntity; menuItems: FacilityItemInformation[] },
    private readonly _formBuilder: FormBuilder,
    public statusService: StatusService,
  ) {
    this.currentFacilityId = this._facilityManager.getCurrentFacility();
    this.menuItems = this.data.menuItems;
    this.baseItemGroupEntity = this.data.itemGroupEntity;
    this.baseItem = structuredClone(this.baseItemGroupEntity.itemGroups[0].items[0]);
    this.baseItemGroups = this.data.itemGroupEntity.itemGroups;
    this.itemChangeState = structuredClone(this.baseItemGroupEntity.itemGroups[0].items[0]);
    this.initialDateRestrictions = this.baseItem.startPickupDate != null && this.baseItem.endPickupDate != null;
    this.adminOrITRole = this.authManager.isCurrentAccountAdminOrITRole();

    this.buildForm();
    this.seedForm();
    this.editItemForm.markAsPristine();
    this.baselineSnapshot = this.editItemForm.getRawValue();
    this.updateDateBounds();

    this.editItemForm.valueChanges.subscribe((_) => {
      this.changeSummary = this.computeChangeSummary();
      this.updateDateBounds();
    });

    this.loadPrepLocations();
  }

  public ngAfterViewInit(): void {
    if (!this.adminOrITRole) {
      this.prepGroup.disable({ emitEvent: false });
      this.handoffTypesForm.disable({ emitEvent: false });
      this.pickupGroup.disable({ emitEvent: false });
    }
  }

  // ---------- form group accessors ----------

  public get prepGroup(): FormGroup {
    return this.editItemForm.get('prep') as FormGroup;
  }
  public get pickupGroup(): FormGroup {
    return this.editItemForm.get('pickup') as FormGroup;
  }
  public get pickupDatesForm(): FormGroup {
    return this.pickupGroup.get('dates') as FormGroup;
  }
  public get quantityGroup(): FormGroup {
    return this.editItemForm.get('quantity') as FormGroup;
  }
  public get limitedQuantityForm(): FormGroup {
    return this.quantityGroup;
  }
  public get soldDatesForm(): FormGroup {
    return this.quantityGroup.get('soldDates') as FormGroup;
  }
  public get handoffTypesForm(): FormGroup {
    return this.editItemForm.get('handoffTypes') as FormGroup;
  }
  public get applyGroup(): FormGroup {
    return this.editItemForm.get('apply') as FormGroup;
  }

  // ---------- calendar date bounds (derived from shared item-date-rules) ----------
  // Only start/end ordering is enforced in the calendar; the 120-day range width and
  // backdating limits are handled by itemDatesValidator so users see an explanatory error
  // instead of silently disabled dates. Recomputed via updateDateBounds() on each form
  // value change rather than exposed as template getters.

  public maxPickupStartDate: Date | null = null;
  public minPickupEndDate: Date | null = null;
  public maxSoldStartDate: Date | null = null;
  public minSoldEndDate: Date | null = null;

  private updateDateBounds(): void {
    const pickupStart = this.pickupDatesForm.get('startDate')?.value;
    const pickupEnd = this.pickupDatesForm.get('endDate')?.value;
    const soldStart = this.soldDatesForm.get('startDate')?.value;
    const soldEnd = this.soldDatesForm.get('endDate')?.value;

    this.maxPickupStartDate = maxStartDate(pickupEnd);
    this.minPickupEndDate = minEndDate(pickupStart);

    this.maxSoldStartDate = maxStartDate(soldEnd);
    this.minSoldEndDate = minEndDate(soldStart);
  }

  // ---------- mode flags backed by form controls ----------

  public get isDateRestricted(): boolean {
    return !!this.pickupGroup.get('isDateRestricted')?.value;
  }
  public get isSellableQuantityEnabled(): boolean {
    return !!this.quantityGroup.get('isSellableQuantityEnabled')?.value;
  }
  public get isMultiItem(): boolean {
    return !!this.applyGroup.get('isMultiItem')?.value;
  }
  public get isMultiStore(): boolean {
    return !!this.applyGroup.get('isMultiStore')?.value;
  }

  // ---------- diff helpers ----------

  private differs(a: unknown, b: unknown): boolean {
    return a !== b;
  }

  private datesDiffer(a: any, b: any): boolean {
    if ((a == null && b == null) || (!a && !b)) return false;
    if (a == null || b == null || !a || !b) return true;
    return new Date(a).getTime() !== new Date(b).getTime();
  }

  private currentPrep() {
    return this.prepGroup.getRawValue();
  }
  private currentPickup() {
    return this.pickupGroup.getRawValue();
  }
  private currentQty() {
    return this.quantityGroup.getRawValue();
  }
  private currentHandoff() {
    return this.handoffTypesForm.getRawValue();
  }

  // ---------- per-field diffs (single source of truth) ----------

  private get prepLocationChanged(): boolean {
    return this.differs(this.currentPrep().prepLocation, this.baselineSnapshot.prep.prepLocation);
  }
  private get leadMinutesChanged(): boolean {
    return this.differs(this.currentPrep().itemChangeLeadHours, this.baselineSnapshot.prep.itemChangeLeadHours);
  }
  private get prepMinutesChanged(): boolean {
    return this.differs(this.currentPrep().prepMinutes, this.baselineSnapshot.prep.prepMinutes);
  }
  private get postPrepMinutesChanged(): boolean {
    return this.differs(this.currentPrep().postPrepMinutes, this.baselineSnapshot.prep.postPrepMinutes);
  }
  private get isDateRestrictedChanged(): boolean {
    return this.currentPickup().isDateRestricted !== this.baselineSnapshot.pickup.isDateRestricted;
  }
  private get startPickupDateChanged(): boolean {
    return this.datesDiffer(this.currentPickup().dates?.startDate, this.baselineSnapshot.pickup.dates?.startDate);
  }
  private get endPickupDateChanged(): boolean {
    return this.datesDiffer(this.currentPickup().dates?.endDate, this.baselineSnapshot.pickup.dates?.endDate);
  }
  private get sellableQuantityEnabledChanged(): boolean {
    return this.currentQty().isSellableQuantityEnabled !== this.baselineSnapshot.quantity.isSellableQuantityEnabled;
  }
  private get sellableQuantityValueChanged(): boolean {
    return this.differs(this.currentQty().sellableQuantity, this.baselineSnapshot.quantity.sellableQuantity);
  }
  private get startSoldDateChanged(): boolean {
    return this.datesDiffer(
      this.currentQty().soldDates?.startDate,
      this.baselineSnapshot.quantity.soldDates?.startDate,
    );
  }
  private get endSoldDateChanged(): boolean {
    return this.datesDiffer(this.currentQty().soldDates?.endDate, this.baselineSnapshot.quantity.soldDates?.endDate);
  }
  private get handoffTypesChanged(): boolean {
    const cur = this.currentHandoff();
    const base = this.baselineSnapshot.handoffTypes;
    return Object.keys(HANDOFF_CONTROL_TO_API).some((k) => cur[k] !== base[k]);
  }

  // ---------- "EDITED" badges (template) — OR of field diffs ----------

  public get isPrepLocationItemEdited(): boolean {
    return this.prepLocationChanged;
  }
  public get isItemTimesItemEdited(): boolean {
    return this.leadMinutesChanged || this.prepMinutesChanged || this.postPrepMinutesChanged;
  }
  public get isPickupDatesItemEdited(): boolean {
    return this.isDateRestrictedChanged || this.startPickupDateChanged || this.endPickupDateChanged;
  }
  public get isAvailableTypesItemEdited(): boolean {
    return this.handoffTypesChanged;
  }
  public get isSellableQtyItemEdited(): boolean {
    return this.sellableQuantityEnabledChanged || this.sellableQuantityValueChanged;
  }
  public get isSoldDatesItemEdited(): boolean {
    return this.startSoldDateChanged || this.endSoldDateChanged;
  }

  public get isConfirmClickable(): boolean {
    if (this.authManager.doesCurrentAccountHaveAnyRole(['Division'])) {
      return this.isClickable && this.limitedQuantityForm.valid;
    }
    return (
      this.isClickable
      && this.handoffTypesForm.valid
      && (!this.isDateRestricted || this.pickupDatesForm.valid)
      && (!this.isSellableQuantityEnabled || (this.limitedQuantityForm.valid && this.soldDatesForm.valid))
    );
  }

  public doesCurrentAccountHaveAnyRole(roles: string[]): boolean {
    return this.authManager.doesCurrentAccountHaveAnyRole(roles);
  }

  // ---------- form construction & seeding ----------

  private buildForm(): void {
    const sellableQuantityBaseline =
      this.baseItemGroupEntity.totalSellableQuantityBaseline ?? this.baseItem.sellableQuantity;

    this.editItemForm = this._formBuilder.group({
      prep: this._formBuilder.group({
        prepLocation: ['', Validators.required],
        itemChangeLeadHours: [0, [Validators.min(0), Validators.max(24)]],
        prepMinutes: [0, [Validators.min(0), Validators.max(1440)]],
        postPrepMinutes: [0, [Validators.min(0), Validators.max(1440)]],
      }),
      pickup: this._formBuilder.group({
        isDateRestricted: [this.initialDateRestrictions],
        dates: this._formBuilder.group(
          {
            startDate: [{ value: '', disabled: !this.initialDateRestrictions }, Validators.required],
            endDate: [{ value: '', disabled: !this.initialDateRestrictions }, Validators.required],
          },
          { validators: itemDatesValidator() },
        ),
      }),
      quantity: this._formBuilder.group({
        isSellableQuantityEnabled: [this.itemChangeState.isSellableQuantityEnabled ?? false],
        sellableQuantity: [
          { value: sellableQuantityBaseline, disabled: !this.itemChangeState.isSellableQuantityEnabled },
          [Validators.required, Validators.min(1)],
        ],
        soldDates: this._formBuilder.group(
          {
            startDate: [{ value: '', disabled: !this.itemChangeState.isSellableQuantityEnabled }, Validators.required],
            endDate: [{ value: '', disabled: !this.itemChangeState.isSellableQuantityEnabled }, Validators.required],
          },
          { validators: itemDatesValidator() },
        ),
      }),
      handoffTypes: this._formBuilder.group(
        {
          carryout: [false],
          curbside: [false],
          delivery: [false],
          kiosk: [false],
          tableside: [false],
        },
        { validators: atLeastOneSelectedValidator() },
      ),
      apply: this._formBuilder.group({
        isMultiItem: [false],
        isMultiStore: [false],
        relatedItems: [null as number | null],
        facilities: [null as number | null],
      }),
    });

    this.pickupGroup.get('isDateRestricted')!.valueChanges.subscribe((enabled: boolean) => {
      this.toggleGroup(this.pickupDatesForm, enabled);
    });
    this.quantityGroup.get('isSellableQuantityEnabled')!.valueChanges.subscribe((enabled: boolean) => {
      const qty = this.quantityGroup.get('sellableQuantity')!;
      enabled ? qty.enable() : qty.disable();
      this.toggleGroup(this.soldDatesForm, enabled);
    });
  }

  private seedForm(): void {
    this.editItemForm.patchValue({
      prep: {
        prepLocation: this.itemChangeState.departmentName,
        itemChangeLeadHours: this.itemChangeState.leadMinutes / 60,
        prepMinutes: this.itemChangeState.prepMinutes,
        postPrepMinutes: this.itemChangeState.postPrepMinutes,
      },
      pickup: {
        dates: this.initialDateRestrictions
          ? { startDate: this.baseItem.startPickupDate, endDate: this.baseItem.endPickupDate }
          : { startDate: '', endDate: '' },
      },
      quantity: {
        soldDates: {
          startDate: this.baseItem.startSoldQuantityDate ?? this.baseItem.startPickupDate ?? '',
          endDate: this.baseItem.endSoldQuantityDate ?? this.baseItem.endPickupDate ?? '',
        },
      },
      handoffTypes: {
        carryout: this.baseItem.allowedHandoffTypes?.includes('store') ?? false,
        curbside: this.baseItem.allowedHandoffTypes?.includes('curbside') ?? false,
        delivery: this.baseItem.allowedHandoffTypes?.includes('delivery') ?? false,
        kiosk: this.baseItem.allowedHandoffTypes?.includes('kiosk') ?? false,
        tableside: this.baseItem.allowedHandoffTypes?.includes('tableside') ?? false,
      },
    });
  }

  private toggleGroup(group: FormGroup, enabled: boolean): void {
    if (enabled) {
      group.enable();
      group.markAllAsTouched();
    } else {
      group.disable();
    }
  }

  private loadPrepLocations(): void {
    this.prepLocationService.getPrepLocations(this.currentFacilityId).subscribe({
      next: (d) => (this.prepLocations = d),
      error: () => this.toastr.error('Unable to load Prep Locations'),
    });
  }

  // ---------- helpers used by template ----------

  public itemValidation(): void {
    const itemsChecked = this.selectedItemGroups.filter((f) => f.isSelected).length;
    this.applyGroup.get('relatedItems')?.setValue(itemsChecked > 0 ? itemsChecked : null);
    this.isClickable = itemsChecked > 0;
  }

  public facilityValidation(): void {
    const facilitiesChecked = this.facilities.filter((f) => f.isSelected).length;
    this.applyGroup.get('facilities')?.setValue(facilitiesChecked > 0 ? facilitiesChecked : null);
    this.isClickable = facilitiesChecked > 0;
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }

  public isMultiAny(): boolean {
    return this.isMultiStore || this.isMultiItem;
  }

  public getSelectedHandoffTypes(): string[] {
    const v = this.handoffTypesForm.value as Record<string, boolean>;
    return Object.keys(HANDOFF_CONTROL_TO_API)
      .filter((k) => v[k])
      .map((k) => HANDOFF_CONTROL_TO_API[k]);
  }

  private computeChangeSummary(): FieldChange[] {
    const ITEM = 'Item Preparation';
    const PICKUP = 'Pickup Dates';
    const QUANT = 'Quantities';

    const prep = this.currentPrep();
    const pickup = this.currentPickup();
    const qty = this.currentQty();
    const handoff = this.currentHandoff();

    const format = 'MMMM dd, yyyy';
    const locale = 'en-US';

    const fmtDate = (d: any) => (d ? formatDate(d, format, locale) : '');

    const changes: [boolean, string, string][] = [
      [this.prepLocationChanged, 'Prep Location', `${ITEM}, Prep Location: ${prep.prepLocation}`],
      [
        this.leadMinutesChanged,
        'Lead Time (Hours)',
        `${ITEM}, Item Times, Lead Time (Hours): ${prep.itemChangeLeadHours}`,
      ],
      [this.prepMinutesChanged, 'Prep Minutes', `${ITEM}, Item Times, Prep Minutes (Minutes): ${prep.prepMinutes}`],
      [
        this.postPrepMinutesChanged,
        'Post-Prep Minutes',
        `${ITEM}, Item Times, Post-Prep Minutes (Minutes): ${prep.postPrepMinutes}`,
      ],
      [
        this.isDateRestrictedChanged,
        'Date Restricted',
        `${PICKUP}, ${PICKUP}: ${pickup.isDateRestricted ? 'Date Range' : 'Any Date'}`,
      ],
      [
        this.startPickupDateChanged && this.isDateRestricted,
        'Start Pickup Date',
        `${PICKUP}, ${PICKUP}, Start Pickup Date: ${fmtDate(pickup.dates?.startDate)}`,
      ],
      [
        this.endPickupDateChanged && this.isDateRestricted,
        'End Pickup Date',
        `${PICKUP}, ${PICKUP}, End Pickup Date: ${fmtDate(pickup.dates?.endDate)}`,
      ],
      [
        this.sellableQuantityEnabledChanged && this.isDateRestricted,
        'Quantity Limit',
        `${PICKUP}, ${QUANT}, Quantity Limit: ${qty.isSellableQuantityEnabled ? 'Limited' : 'Unlimited'}`,
      ],
      [
        this.sellableQuantityValueChanged && this.isDateRestricted,
        'Sellable Quantity Amount',
        `${PICKUP}, ${QUANT}, Sellable Quantity Amount: ${qty.sellableQuantity}`,
      ],
      [
        this.startSoldDateChanged && this.isDateRestricted,
        'Start Sold Date',
        `${PICKUP}, Start Sold Date: ${fmtDate(qty.soldDates?.startDate)}`,
      ],
      [
        this.endSoldDateChanged && this.isDateRestricted,
        'End Sold Date',
        `${PICKUP}, End Sold Date: ${fmtDate(qty.soldDates?.endDate)}`,
      ],
      [this.handoffTypesChanged, 'Handoff Types', `Available Handoff Types: ${this.handoffListFrom(handoff)}`],
    ];
    return changes.reduce<FieldChange[]>((acc, [changed, label, description]) => {
      if (changed) acc.push({ label, description });
      return acc;
    }, []);
  }

  private handoffListFrom(group: Record<string, boolean>): string {
    return Object.keys(HANDOFF_CONTROL_TO_API)
      .filter((key) => group[key])
      .map((key) => key.charAt(0).toUpperCase() + key.slice(1))
      .join(', ');
  }

  // ---------- save flow ----------

  public updateFacilityItems(): void {
    if (!this.validateBeforeUpdate()) {
      return;
    }

    this.isClickable = false;
    this.updatedItemGroups = [];

    const selectedItems = this.getSelectedItems();
    const selectedFacilities = AdminUIHelpers.getSelectedFacilityIds(this.currentFacilityId, this.facilities);

    if (selectedItems.length === 0) {
      this.toastr.warning('No items selected');
      this.isClickable = true;
      return;
    }
    if (this.isMultiStore && this.facilities.every((f) => !f.isSelected)) {
      this.toastr.warning('No facilities selected');
      this.isClickable = true;
      return;
    }

    this.updateItemChangeState();

    const patchOperation = this.buildPatchOperation(selectedItems, selectedFacilities);
    this.executePatchOperation(patchOperation, selectedItems);
  }

  private validateBeforeUpdate(): boolean {
    if (this.prepGroup.status === 'INVALID') {
      this.toastr.warning('Information missing from Item');
      return false;
    }
    return true;
  }

  private getSelectedItems(): ItemGroup[] {
    if (this.isMultiItem) {
      return this.selectedItemGroups.filter((item) => item.isSelected);
    }
    return this.baseItemGroups;
  }

  private calculateLeadMinutesFromLeadHours(): number {
    return (this.prepGroup.get('itemChangeLeadHours')?.value ?? 0) * 60;
  }

  private updateItemChangeState(): void {
    const prep = this.prepGroup.getRawValue();
    const pickup = this.pickupDatesForm.getRawValue();
    const sold = this.soldDatesForm.getRawValue();
    const qty = this.quantityGroup.getRawValue();

    this.itemChangeState.departmentName = prep.prepLocation;
    this.itemChangeState.leadMinutes = (prep.itemChangeLeadHours ?? 0) * 60;
    this.itemChangeState.prepMinutes = prep.prepMinutes;
    this.itemChangeState.postPrepMinutes = prep.postPrepMinutes;

    this.itemChangeState.startPickupDate = pickup.startDate;
    this.itemChangeState.endPickupDate = pickup.endDate;
    this.itemChangeState.startSoldQuantityDate = sold.startDate;
    this.itemChangeState.endSoldQuantityDate = sold.endDate;

    this.itemChangeState.isSellableQuantityEnabled = qty.isSellableQuantityEnabled;
    this.itemChangeState.sellableQuantity =
      qty.isSellableQuantityEnabled && qty.sellableQuantity > 0 ? qty.sellableQuantity : 0;
  }

  // ---------- per-field patch construction ----------

  private buildOperationsForCurrentChanges(totalQuantityForItem: number): Operation[] {
    const ops: Operation[] = [];
    const state = this.itemChangeState;
    const leadMinutes = this.calculateLeadMinutesFromLeadHours();

    if (this.leadMinutesChanged) {
      ops.push({ op: 'replace', path: '/leadMinutes', value: leadMinutes });
    }
    if (this.prepMinutesChanged) {
      ops.push({ op: 'replace', path: '/prepMinutes', value: state.prepMinutes });
    }
    if (this.postPrepMinutesChanged) {
      ops.push({ op: 'replace', path: '/postPrepMinutes', value: state.postPrepMinutes });
    }
    if (this.prepLocationChanged) {
      ops.push({ op: 'replace', path: '/prepLocationTypeName', value: state.departmentName });
    }
    if (this.handoffTypesChanged) {
      ops.push({ op: 'replace', path: '/allowedHandoffTypes', value: this.getSelectedHandoffTypes() });
    }

    // Turning OFF date restriction -> remove all restriction-related fields.
    if (this.isDateRestrictedChanged && !this.isDateRestricted) {
      ops.push({ op: 'remove', path: '/startPickupDate' });
      ops.push({ op: 'remove', path: '/endPickupDate' });
      ops.push({ op: 'remove', path: '/sellableQuantity' });
      ops.push({ op: 'remove', path: '/startSoldQuantityDate' });
      ops.push({ op: 'remove', path: '/endSoldQuantityDate' });
      ops.push({ op: 'replace', path: '/isSellableQuantityEnabled', value: false });
      return ops;
    }

    // Restriction is on (was-on or just-turned-on).
    if (this.isDateRestricted) {
      const restrictionJustTurnedOn = this.isDateRestrictedChanged;
      const pickupDatesChanged = this.startPickupDateChanged || this.endPickupDateChanged;
      if (restrictionJustTurnedOn || pickupDatesChanged) {
        ops.push({ op: 'replace', path: '/startPickupDate', value: state.startPickupDate });
        ops.push({ op: 'replace', path: '/endPickupDate', value: state.endPickupDate });
      }

      if (this.sellableQuantityEnabledChanged) {
        ops.push({
          op: 'replace',
          path: '/isSellableQuantityEnabled',
          value: state.isSellableQuantityEnabled,
        });
        if (!state.isSellableQuantityEnabled) {
          ops.push({ op: 'remove', path: '/sellableQuantity' });
          ops.push({ op: 'remove', path: '/startSoldQuantityDate' });
          ops.push({ op: 'remove', path: '/endSoldQuantityDate' });
        }
      }

      if (state.isSellableQuantityEnabled) {
        const justEnabled = this.sellableQuantityEnabledChanged;
        const soldDatesChanged = this.startSoldDateChanged || this.endSoldDateChanged;
        const anySoldChanged = this.sellableQuantityValueChanged || soldDatesChanged;
        if (justEnabled || anySoldChanged) {
          ops.push({ op: 'replace', path: '/sellableQuantity', value: totalQuantityForItem });
          ops.push({ op: 'replace', path: '/startSoldQuantityDate', value: state.startSoldQuantityDate });
          ops.push({ op: 'replace', path: '/endSoldQuantityDate', value: state.endSoldQuantityDate });
        }
      }
    }

    return ops;
  }

  private buildPatchOperation(
    selectedItems: ItemGroup[],
    selectedFacilities: string[],
  ): Observable<PatchResult> | null {
    const selectedHandoffTypes = this.getSelectedHandoffTypes();
    const sellableQuantityIsBeingPatched =
      this.isDateRestricted
      && this.itemChangeState.isSellableQuantityEnabled
      && (this.sellableQuantityEnabledChanged
        || this.sellableQuantityValueChanged
        || this.startSoldDateChanged
        || this.endSoldDateChanged);

    const quantityGroups = sellableQuantityIsBeingPatched
      ? this.groupItemsByQuantity(selectedItems)
      : new Map<number, ItemGroup[]>([[0, selectedItems]]);

    const batchObservables: Observable<PatchResult>[] = [];

    for (const [quantity, groupItems] of quantityGroups) {
      const operations = this.buildOperationsForCurrentChanges(quantity);
      if (operations.length === 0) continue;

      const commerceItemNumbers: string[] = [];
      groupItems.forEach((itemGroup) => {
        itemGroup.items.forEach((item) => {
          const totalQuantityForItem = this.calculateTotalQuantityForItem(item);
          commerceItemNumbers.push(item.commerceItemNumber);
          this.updateItemInPlaceSync(item, selectedHandoffTypes, totalQuantityForItem);
          if (!this.updatedItemGroups.includes(itemGroup)) {
            this.updatedItemGroups.push(itemGroup);
          }
        });
      });

      const patchRequest: BatchPatchRequest = {
        facilityIds: selectedFacilities,
        patches: { operations, commerceItemNumbers },
      };
      batchObservables.push(this.createBatchPatchObservable(patchRequest, selectedFacilities, groupItems));
    }

    if (batchObservables.length === 0) {
      this.toastr.warning('No changes detected');
      this.isClickable = true;
      return null;
    }

    this.statusService.forceLoader = true;
    return forkJoin(batchObservables).pipe(map((results) => this.combinePatchResults(results)));
  }

  private groupItemsByQuantity(selectedItems: ItemGroup[]): Map<number, ItemGroup[]> {
    const groups = new Map<number, ItemGroup[]>();
    for (const itemGroup of selectedItems) {
      const quantity = this.calculateTotalQuantityForItem(itemGroup.items[0]);
      const existing = groups.get(quantity);
      if (existing) {
        existing.push(itemGroup);
      } else {
        groups.set(quantity, [itemGroup]);
      }
    }
    return groups;
  }

  private combinePatchResults(results: PatchResult[]): PatchResult {
    return results.reduce(
      (combined, result) => ({
        isError: combined.isError || result.isError,
        error: combined.error ?? result.error,
        successCount: (combined.successCount ?? 0) + (result.successCount ?? 0),
        errorCount: (combined.errorCount ?? 0) + (result.errorCount ?? 0),
      }),
      { isError: false, error: null, successCount: 0, errorCount: 0 } as PatchResult,
    );
  }

  private createBatchPatchObservable(
    patchRequest: BatchPatchRequest,
    selectedFacilities: string[],
    selectedItems: ItemGroup[],
  ): Observable<PatchResult> {
    const totalItemCount = selectedItems.reduce((sum, ig) => sum + ig.items.length, 0);
    const totalOperations = totalItemCount * selectedFacilities.length;

    return this.itemService.patchOrderableItems(patchRequest).pipe(
      switchMap((response: any) => {
        const instanceId = response.InstanceId || response.instanceId;
        const code = response.Code || response.code;
        if (response && instanceId && code) {
          this.toastr.info('Processing items, please wait...', 'Update in Progress', {
            disableTimeOut: true,
            closeButton: true,
          });
          return timer(0, 5000).pipe(
            switchMap(() => this.itemService.getDurableStatus(instanceId, code)),
            takeWhile((s: any) => s.status === 202, true),
            map((s: any) => {
              if (s.status === 200 || s.status === 500) this.toastr.clear();
              if (s.status === 200) {
                return { isError: false, error: null, successCount: totalOperations, errorCount: 0 };
              }
              if (s.status === 500) {
                return {
                  isError: true,
                  error: 'Batch operation failed',
                  successCount: 0,
                  errorCount: totalOperations,
                };
              }
              return { isError: false, error: null, successCount: 0, errorCount: 0 };
            }),
            catchError((err) => {
              this.toastr.clear();
              const errObj: PatchResult = {
                isError: true,
                error: 'Error checking process status',
                successCount: 0,
                errorCount: totalOperations,
              };
              this.logger.logTrace('batch patch polling error', errObj);
              return of(errObj);
            }),
          );
        }
        return of({
          isError: true,
          error: 'Invalid response from server',
          successCount: 0,
          errorCount: totalOperations,
        } as PatchResult);
      }),
      catchError((err) => {
        this.toastr.clear();
        const errObj: PatchResult = {
          isError: true,
          error: err,
          successCount: 0,
          errorCount: totalOperations,
        };
        this.logger.logTrace('batch patch error', errObj);
        return of(errObj);
      }),
    );
  }

  private calculateTotalQuantityForItem(item: FacilityItemInformation): number {
    const divisor =
      item.commodityGroupUnitQuantity === 0 || item.commodityGroupUnitQuantity === undefined
        ? 1
        : item.commodityGroupUnitQuantity;
    return Math.floor(this.itemChangeState.sellableQuantity / divisor);
  }

  private updateItemInPlaceSync(
    item: FacilityItemInformation,
    selectedHandoffTypes: string[],
    totalQuantityForItem: number,
  ): void {
    if (this.leadMinutesChanged) {
      item.leadMinutes = this.calculateLeadMinutesFromLeadHours();
    }
    if (this.prepMinutesChanged) {
      item.prepMinutes = this.itemChangeState.prepMinutes;
    }
    if (this.postPrepMinutesChanged) {
      item.postPrepMinutes = this.itemChangeState.postPrepMinutes;
    }
    if (this.prepLocationChanged) {
      item.departmentName = this.itemChangeState.departmentName;
    }
    if (this.handoffTypesChanged) {
      item.allowedHandoffTypes = selectedHandoffTypes;
    }

    if (this.isDateRestrictedChanged && !this.isDateRestricted) {
      item.startPickupDate = null;
      item.endPickupDate = null;
      item.sellableQuantity = 0;
      item.isSellableQuantityEnabled = false;
      item.startSoldQuantityDate = undefined;
      item.endSoldQuantityDate = undefined;
      return;
    }

    const pickupDatesChanged = this.startPickupDateChanged || this.endPickupDateChanged;
    const restrictionJustTurnedOn = this.isDateRestrictedChanged && this.isDateRestricted;
    if (pickupDatesChanged || restrictionJustTurnedOn) {
      item.startPickupDate = this.itemChangeState.startPickupDate;
      item.endPickupDate = this.itemChangeState.endPickupDate;
    }

    if (this.sellableQuantityEnabledChanged) {
      item.isSellableQuantityEnabled = this.itemChangeState.isSellableQuantityEnabled;
      if (!this.itemChangeState.isSellableQuantityEnabled) {
        item.sellableQuantity = 0;
        item.startSoldQuantityDate = undefined;
        item.endSoldQuantityDate = undefined;
      }
    }

    const justEnabled = this.sellableQuantityEnabledChanged && this.itemChangeState.isSellableQuantityEnabled;
    const soldDatesChanged = this.startSoldDateChanged || this.endSoldDateChanged;
    const anySoldChanged = this.sellableQuantityValueChanged || soldDatesChanged;
    if (anySoldChanged || justEnabled) {
      item.sellableQuantity =
        this.isDateRestricted && this.itemChangeState.isSellableQuantityEnabled ? totalQuantityForItem : 0;
      item.startSoldQuantityDate = this.itemChangeState.startSoldQuantityDate;
      item.endSoldQuantityDate = this.itemChangeState.endSoldQuantityDate;
    }
  }

  private executePatchOperation(patchOperation: Observable<PatchResult> | null, selectedItems: ItemGroup[]): void {
    if (patchOperation === null) {
      this.statusService.forceLoader = false;
      this.isClickable = true;
      return;
    }

    this.updatedItemGroups = selectedItems.map(
      (itemGroup) =>
        ({
          isAvailable: itemGroup.isAvailable,
          isExternalItemNumberGroup: itemGroup.isExternalItemNumberGroup,
          itemNumber: itemGroup.itemNumber,
          items: itemGroup.items,
          totalSellableQuantity: this.itemChangeState.sellableQuantity,
        }) as ItemGroup,
    );

    const itemGroupEntities = this.createItemGroupEntities();

    patchOperation.subscribe({
      next: (response) => {
        this.statusService.forceLoader = false;
        this.isClickable = true;
        const success = this.saveToastrLogic(response);
        if (success) {
          const fullSuccess = !response.isError && (response.errorCount ?? 0) === 0;
          if (fullSuccess) {
            this.baselineSnapshot = this.editItemForm.getRawValue();
          }
          this.dialogRef.close(itemGroupEntities);
        }
      },
      error: (err) => {
        this.statusService.forceLoader = false;
        this.toastr.error('Error while updating Items: ' + err);
        this.isClickable = true;
      },
    });
  }

  private createItemGroupEntities(): ItemGroupEntity[] {
    if (this.baseItemGroupEntity.isCommodityGroup) {
      const updatedItems: FacilityItemInformation[] = [];
      this.updatedItemGroups.forEach((ig) => updatedItems.push(...ig.items));
      return ItemGroupHelpers.getCommodityItemGroups(updatedItems);
    }
    return ItemGroupHelpers.mapItemGroupsToItemGroupEntities(this.updatedItemGroups);
  }

  private saveToastrLogic(response: PatchResult): boolean {
    const totalSuccess = response.successCount ?? 0;
    const totalErrors = response.errorCount ?? 0;

    if (response.isError || totalErrors > 0) {
      if (totalSuccess > 0) {
        this.toastr.warning(
          `${totalSuccess} item(s) were updated successfully, but ${totalErrors} failed`,
          'Partial Success',
          { timeOut: 15000 },
        );
      } else {
        this.toastr.error('All items failed to update. Please try again.');
        return false;
      }
    } else {
      this.toastr.success(`Successfully updated ${totalSuccess} item(s)!`);
    }
    return true;
  }
}
